import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { ProductThumbnail } from "@/components/shared/product-thumbnail";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  AmazonProductLink,
  PaginationFooter,
  PaneHeading,
  ProductRowsSkeleton,
} from "./PickerParts";
import type { ProductListingBrowse } from "./use-product-listing-browse";
import type { ProductSelection } from "./use-product-selection";

interface ParentGroupPaneProps {
  browse: ProductListingBrowse;
  selection: ProductSelection;
  marketplaceId: string;
}

/** Left pane of the three-pane layout: variation groups, each selectable as a whole. */
export function ParentGroupPane({ browse, selection, marketplaceId }: ParentGroupPaneProps) {
  const { t } = useTranslation("campaignCreation");
  const { groups, activeGroup, hasSkuFilter, searchSettling, showGroup } = browse;
  const { selected, matchingSelected, matchingTotal, matchingCurrent, matchingSettled } = selection;
  return (
    <section
      className="flex min-w-0 flex-col overflow-hidden rounded-[10px] border border-line bg-surface"
      aria-label={t("products.parentPane")}
    >
      <PaneHeading title={t("products.parentAsin")}>
        <Checkbox
          aria-label={t("products.selectAll")}
          disabled={
            selection.bulkPending ||
            !browse.bulkSelectable ||
            !matchingSettled ||
            groups.isError ||
            groups.isPending ||
            groups.isPlaceholderData ||
            searchSettling
          }
          checked={
            matchingCurrent &&
            matchingSelected.length > 0 &&
            matchingSelected.length === matchingTotal
          }
          indeterminate={
            matchingCurrent &&
            matchingSelected.length > 0 &&
            matchingSelected.length < matchingTotal
          }
          onCheckedChange={(checked) => {
            if (checked) void selection.selectFiltered();
            else selection.removeSkus(matchingSelected.map((product) => product.sku));
          }}
        />
      </PaneHeading>
      {groups.isError ? (
        <ErrorState message={t("products.loadError")} onRetry={() => void groups.refetch()} />
      ) : groups.isPending || groups.isPlaceholderData ? (
        <ProductRowsSkeleton label={t("products.loading")} />
      ) : groups.data?.data.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">{t("products.noMatches")}</p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto paper-scrollbar">
        <Table className="table-fixed">
          <colgroup>
            <col className="w-9" />
            <col className="w-16" />
            <col />
          </colgroup>
          <TableBody>
            {!groups.isPlaceholderData &&
              groups.data?.data.map((group) => {
                const groupSelection = hasSkuFilter
                  ? matchingCurrent
                    ? matchingSelected
                    : []
                  : selected;
                const groupSelected = groupSelection.filter(
                  (product) => product.variationGroupKey === group.variationGroupKey,
                ).length;
                const selectableCount = group.selectableSkuCount ?? group.matchingSkuCount;
                return (
                  <TableRow
                    key={group.variationGroupKey}
                    aria-selected={activeGroup === group.variationGroupKey}
                    className="cursor-pointer aria-selected:bg-subtle"
                    onClick={() => showGroup(group.variationGroupKey)}
                  >
                    <TableCell className="w-9" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        aria-label={t("products.selectParent", {
                          parent: group.variationGroupKey,
                        })}
                        disabled={
                          selection.bulkPending ||
                          !browse.bulkSelectable ||
                          (hasSkuFilter && !matchingSettled) ||
                          selectableCount === 0 ||
                          groups.isError ||
                          groups.isPlaceholderData ||
                          searchSettling
                        }
                        checked={groupSelected > 0 && groupSelected >= selectableCount}
                        indeterminate={groupSelected > 0 && groupSelected < selectableCount}
                        onCheckedChange={(checked) => {
                          const partial = groupSelected > 0 && groupSelected < selectableCount;
                          // Partial groups clear; see the unified table's group row.
                          if (checked && !partial)
                            void selection.selectFiltered(group.variationGroupKey);
                          else
                            selection.removeSkus(
                              groupSelection
                                .filter(
                                  (product) =>
                                    product.variationGroupKey === group.variationGroupKey,
                                )
                                .map((product) => product.sku),
                            );
                        }}
                      />
                    </TableCell>
                    <TableCell className="w-16 py-4 pr-0">
                      <ProductThumbnail
                        imageUrl={group.imageUrl}
                        label={group.title ?? group.variationGroupKey}
                        asin={group.parentAsin ?? group.singleListing?.asin}
                        sku={group.singleListing?.sku}
                        productStatus={group.singleListing?.productStatus}
                      />
                    </TableCell>
                    <TableCell className="min-w-0 overflow-hidden whitespace-normal py-4">
                      <button
                        type="button"
                        className="block w-full truncate text-left text-sm font-medium leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => showGroup(group.variationGroupKey)}
                      >
                        {group.title ?? group.variationGroupKey}
                      </button>
                      {group.groupKind && group.groupKind !== "unknown" ? (
                        <p className="text-xs leading-[18px] text-muted-foreground">
                          {t(`products.groupKind.${group.groupKind}`)}
                        </p>
                      ) : null}
                      <p className="mt-1 flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
                        {group.groupKind === "parent" && group.parentAsin ? (
                          <>
                            <span className="truncate">{group.parentAsin}</span>
                            <AmazonProductLink
                              asin={group.parentAsin}
                              marketplaceId={marketplaceId}
                            />
                            <span aria-hidden="true">|</span>
                          </>
                        ) : null}
                        <span>{t("products.selectableCount", { count: selectableCount })}</span>
                      </p>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
      <PaginationFooter
        page={browse.groupPage}
        total={groups.data?.pagination.total ?? 0}
        pageSize={groups.data?.pagination.pageSize ?? 20}
        onPageChange={browse.setGroupPage}
      />
    </section>
  );
}
